import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, ArrowLeft, BarChart3, BookOpen, CalendarDays, ClipboardList, Cpu, Download, Globe, Image as ImageIcon, LayoutDashboard, Library,
  LogOut, Mail, Menu, Send, Server, Settings, ShieldCheck, Sparkles, X, Bookmark, Archive, Share2, GraduationCap, Sunrise,
} from "lucide-react";
import GlobalSearch from "@/components/admin/GlobalSearch";
import { adminApi } from "@/lib/adminApi";
import { clearAdminToken } from "@/components/admin/AdminGuard";
import NotificationCenter from "@/components/admin/NotificationCenter";
import AdminLangToggle from "@/components/admin/AdminLangToggle";
import { useT } from "@/i18n/LanguageContext";

/**
 * AdminShell v3 — expanded operating-system chrome.
 *
 * Desktop: side rail with 9 primary sections + Logout. Notification bell in
 * the top-right, wordmark on the left. NavLink `isActive` gives current-page
 * highlighting for free.
 * Mobile: hamburger button → slide-in drawer with the same items.
 */

const NAV_GROUPS = [
  { labelKey: "admin.nav.group.content", items: [
    { to: "/admin/dashboard",          labelKey: "admin.nav.dashboard",         icon: LayoutDashboard, end: true },
    { to: "/admin/editorial", labelKey: "admin.nav.editorial", icon: ClipboardList },
    { to: "/admin/publishing-studio",  labelKey: "admin.nav.publishingStudio",  icon: Share2 },
    { to: "/admin/journal",   labelKey: "admin.nav.journal",   icon: BookOpen },
    { to: "/admin/content-calendar", labelKey: "admin.nav.contentCalendar", icon: CalendarDays },
    { to: "/admin/lexicon",   labelKey: "admin.nav.lexicon",   icon: Bookmark },
    { to: "/admin/daily-inspiration", labelKey: "admin.nav.dailyInspiration", icon: Sunrise },
  ]},
  { labelKey: "admin.nav.group.academy", items: [
    { to: "/admin/students",   labelKey: "admin.nav.students",  icon: GraduationCap },
    { to: "/admin/founders",   labelKey: "admin.nav.founders",  icon: ShieldCheck },
    { to: "/admin/academy-logic", labelKey: "admin.nav.logicCms", icon: BookOpen },
    { to: "/admin/academy-ethics", labelKey: "admin.nav.ethicsCms", icon: BookOpen },
    { to: "/admin/academy-physics", labelKey: "admin.nav.physicsCms", icon: BookOpen },
    { to: "/admin/library",   labelKey: "admin.nav.library",   icon: Library },
  ]},
  { labelKey: "admin.nav.group.media", items: [
    { to: "/admin/vault",      labelKey: "admin.nav.vault",     icon: Archive },
    { to: "/admin/site-media", labelKey: "admin.nav.siteMedia", icon: ImageIcon },
    { to: "/admin/academy-logic-media", labelKey: "admin.nav.logicMedia", icon: ImageIcon },
    { to: "/admin/academy-physics-media", labelKey: "admin.nav.physicsMedia", icon: ImageIcon },
    { to: "/admin/academy-ethics-media", labelKey: "admin.nav.ethicsMedia", icon: ImageIcon },
  ]},
  { labelKey: "admin.nav.group.marketing", items: [
    { to: "/admin/autoseo",   labelKey: "admin.nav.autoseo",   icon: Sparkles },
    { to: "/admin/newsletter", labelKey: "admin.nav.newsletter", icon: Send },
    { to: "/admin/seo-center", labelKey: "admin.nav.seoCenter", icon: Globe },
    { to: "/admin/analytics", labelKey: "admin.nav.analytics", icon: BarChart3 },
    { to: "/admin/downloads", labelKey: "admin.nav.downloads", icon: Download },
  ]},
  { labelKey: "admin.nav.group.system", items: [
    { to: "/admin/messages",  labelKey: "admin.nav.messages",  icon: Mail },
    { to: "/admin/settings",  labelKey: "admin.nav.settings",  icon: Settings },
    { to: "/admin/ai-jobs",   labelKey: "admin.nav.aiJobs",    icon: Cpu },
    { to: "/admin/system",    labelKey: "admin.nav.system",    icon: Server },
  ]},
];

const NAV = NAV_GROUPS.flatMap((g) => g.items);

const railItem = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 text-[11px] tracking-[0.24em] uppercase transition-colors border-l-2 ${
    isActive
      ? "text-gold border-gold bg-gold/5"
      : "text-bone/60 border-transparent hover:text-gold hover:border-gold/40"
  }`;

const drawerItem = ({ isActive }) =>
  `flex items-center gap-3 px-1 py-3 text-[13px] tracking-[0.24em] uppercase transition-colors ${
    isActive ? "text-gold" : "text-bone/75 hover:text-gold"
  }`;

export default function AdminShell({ title, children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Pull activity once per session so the notification bell has data.
  useEffect(() => {
    let cancelled = false;
    adminApi.activity(12)
      .then((d) => { if (!cancelled) setEvents(d.events || []); })
      .catch(() => { /* fail silent — bell shows 0 */ });
    return () => { cancelled = true; };
  }, [pathname]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin", { replace: true });
  };

  const currentLabel = useMemo(() => {
    const hit = NAV.find((n) => n.end ? pathname === n.to : pathname.startsWith(n.to));
    return hit ? t(hit.labelKey) : "";
  }, [pathname, t]);

  return (
    <div className="min-h-screen bg-[#050505] text-bone">
      {/* ---- Top bar ---- */}
      <header
        data-testid="admin-shell-header"
        className="fixed top-0 inset-x-0 z-50 bg-black/85 backdrop-blur-xl border-b border-gold/15"
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          {/* MOBILE LEFT: hamburger button (separate) + clickable wordmark */}
          <div className="lg:hidden flex items-center gap-3">
            <button
              type="button"
              data-testid="admin-mobile-menu-btn"
              onClick={() => setOpen(true)}
              className="text-bone hover:text-gold transition-colors"
              aria-label="Open admin menu"
            >
              <Menu size={18} strokeWidth={1.5} />
            </button>
            <Link
              to="/admin/dashboard"
              data-testid="admin-wordmark-mobile"
              className="font-serif text-sm tracking-wide text-bone hover:text-gold transition-colors"
            >
              {t("admin.brand").replace(" · ", " ")}
            </Link>
          </div>

          {/* DESKTOP LEFT: clickable wordmark + section breadcrumb */}
          <Link
            to="/admin/dashboard"
            data-testid="admin-wordmark-desktop"
            className="hidden lg:flex items-center gap-4 group"
          >
            <ShieldCheck size={16} strokeWidth={1.5} className="text-gold" />
            <span className="font-serif text-bone text-sm tracking-wide group-hover:text-gold transition-colors">
              {t("admin.brand")}
            </span>
            {currentLabel && (
              <>
                <span className="text-bone/30">/</span>
                <span className="text-[11px] tracking-[0.28em] uppercase text-gold/80">
                  {currentLabel}
                </span>
              </>
            )}
          </Link>

          <div className="flex items-center gap-3 md:gap-4">
            <div className="hidden lg:block"><GlobalSearch /></div>
            {/* Permanent Home escape hatch — desktop only (mobile lives in drawer) */}
            <Link
              to="/"
              data-testid="admin-back-home-desktop"
              className="hidden lg:inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold transition-colors"
              title={t("admin.nav.backHome")}
            >
              <ArrowLeft size={13} strokeWidth={1.5} />
              {t("admin.nav.backHome")}
            </Link>
            <div className="hidden md:block"><AdminLangToggle /></div>
            <NotificationCenter events={events} />
            {/* Desktop logout is inline; mobile version lives in the drawer */}
            <button
              type="button"
              onClick={logout}
              data-testid="admin-nav-logout"
              className="hidden lg:inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-bone/70 hover:text-red-400 transition-colors"
            >
              <LogOut size={13} strokeWidth={1.5} />
              {t("admin.nav.logout")}
            </button>
          </div>
        </div>
      </header>

      {/* ---- Desktop side rail ---- */}
      <aside
        className="hidden lg:block fixed left-0 top-16 bottom-0 w-56 border-r border-gold/10 bg-black/60 backdrop-blur-md z-40 overflow-y-auto"
        data-testid="admin-side-rail"
      >
        <nav className="py-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey} className="mb-4">
              <p className="px-4 pb-1.5 text-[9px] tracking-[0.32em] uppercase text-gold/50">
                {t(group.labelKey)}
              </p>
              {group.items.map((item) => {
                const Icon = item.icon;
                const label = t(item.labelKey);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={railItem}
                    data-testid={`admin-nav-${item.labelKey.split(".").pop()}`}
                  >
                    <Icon size={14} strokeWidth={1.5} />
                    {label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* ---- Mobile drawer ---- */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="admin-mobile-drawer">
          <button
            type="button"
            aria-label={t("admin.action.close")}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <div className="absolute top-0 left-0 h-full w-[82%] max-w-[320px] bg-[#080808] border-r border-gold/20 shadow-2xl px-6 py-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck size={15} strokeWidth={1.5} className="text-gold" />
                <span className="font-serif text-bone text-[13px] tracking-wide">
                  Command Center
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="admin-mobile-close-btn"
                className="text-bone/70 hover:text-gold"
                aria-label={t("admin.action.close")}
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Language toggle inside the drawer — mobile-only surface */}
            <div className="mb-4"><GlobalSearch mobile /></div>
            <div className="mb-6 pb-4 border-b border-white/5 flex items-center justify-between">
              <span className="text-[10px] tracking-[0.28em] uppercase text-bone/50">
                {t("admin.label.language")}
              </span>
              <AdminLangToggle testId="admin-mobile-lang-toggle" />
            </div>

            <nav className="flex flex-col divide-y divide-white/5">
              {/* Persistent escape hatch — placed ABOVE Dashboard per spec */}
              <Link
                to="/"
                data-testid="admin-mobile-back-home"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-1 py-3 text-[13px] tracking-[0.24em] uppercase text-gold hover:text-bone transition-colors"
              >
                <ArrowLeft size={14} strokeWidth={1.5} />
                {t("admin.nav.backHome")}
              </Link>
              {NAV_GROUPS.map((group) => (
                <div key={group.labelKey} className="py-1">
                  <p className="pt-3 pb-1 text-[9px] tracking-[0.32em] uppercase text-gold/50">
                    {t(group.labelKey)}
                  </p>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const label = t(item.labelKey);
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={drawerItem}
                        data-testid={`admin-mobile-nav-${item.labelKey.split(".").pop()}`}
                      >
                        <Icon size={14} strokeWidth={1.5} />
                        {label}
                      </NavLink>
                    );
                  })}
                </div>
              ))}
              <button
                type="button"
                onClick={logout}
                data-testid="admin-mobile-nav-logout"
                className="text-left py-3 text-[13px] tracking-[0.24em] uppercase text-bone/80 hover:text-red-400 transition-colors inline-flex items-center gap-3"
              >
                <LogOut size={13} strokeWidth={1.5} /> {t("admin.nav.logout")}
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* ---- Content ---- */}
      <main className="pt-20 md:pt-24 pb-24 lg:pl-56">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {title && (
            <h1 className="font-serif text-3xl md:text-4xl text-bone mb-10 mt-4" data-testid="admin-page-title">
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

// Icons re-exported for consumers if useful (currently unused externally).
export { Activity };
